import { useState, useEffect, useCallback, useRef } from 'react';
import { cmsApi } from '../lib/api';

/**
 * Hook to fetch CMS content for a page with fallback to defaults
 * @param {string} slug - The page slug (e.g., 'home', 'about')
 * @param {object} defaultContent - Fallback content if CMS fails or returns null
 */
export const useCMSContent = (slug, defaultContent = {}) => {
  const [content, setContent] = useState(null);
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCMS, setIsFromCMS] = useState(false);
  
  // Use ref to avoid re-fetching on every render
  const hasFetched = useRef(false);
  const defaultContentRef = useRef(defaultContent);

  useEffect(() => {
    // Only fetch once per slug
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await cmsApi.getPublicPage(slug);
        const pageData = response.data;
        
        if (pageData && pageData.blocks && pageData.blocks.length > 0) {
          // Convert blocks array to object keyed by block_key for easy access
          const blocksMap = {};
          pageData.blocks.forEach(block => {
            if (block.is_enabled) {
              blocksMap[block.block_key] = block.content_json;
            }
          });
          
          setContent(pageData);
          setBlocks(blocksMap);
          setIsFromCMS(true);
        } else {
          // No CMS content, use defaults
          setContent(null);
          setBlocks(defaultContentRef.current);
          setIsFromCMS(false);
        }
      } catch (err) {
        console.log(`CMS content not available for ${slug}, using defaults`);
        setError(err);
        setContent(null);
        setBlocks(defaultContentRef.current);
        setIsFromCMS(false);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, [slug]);

  /**
   * Get content for a specific block with fallback
   * @param {string} blockKey - The block key (e.g., 'hero', 'services_preview')
   * @param {object} fallback - Fallback content for this specific block
   */
  const getBlock = useCallback((blockKey, fallback = {}) => {
    return blocks[blockKey] || fallback;
  }, [blocks]);

  /**
   * Get a specific field from a block with language support
   * @param {string} blockKey - The block key
   * @param {string} field - The field name (without _es suffix)
   * @param {string} language - 'en' or 'es'
   * @param {string} fallback - Fallback value
   */
  const getField = useCallback((blockKey, field, language = 'en', fallback = '') => {
    const block = blocks[blockKey];
    if (!block) return fallback;
    
    if (language === 'es') {
      return block[`${field}_es`] || block[field] || fallback;
    }
    return block[field] || fallback;
  }, [blocks]);

  const refetch = useCallback(async () => {
    hasFetched.current = false;
    setLoading(true);
    
    try {
      const response = await cmsApi.getPublicPage(slug);
      const pageData = response.data;
      
      if (pageData && pageData.blocks && pageData.blocks.length > 0) {
        const blocksMap = {};
        pageData.blocks.forEach(block => {
          if (block.is_enabled) {
            blocksMap[block.block_key] = block.content_json;
          }
        });
        
        setContent(pageData);
        setBlocks(blocksMap);
        setIsFromCMS(true);
      }
    } catch (err) {
      console.error('Error refetching CMS content:', err);
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [slug]);

  return {
    content,
    blocks,
    loading,
    error,
    isFromCMS,
    getBlock,
    getField,
    refetch,
  };
};

/**
 * Hook to fetch CMS navigation with fallback
 */
export const useCMSNavigation = (defaultNav = []) => {
  const [navigation, setNavigation] = useState(defaultNav);
  const [loading, setLoading] = useState(true);
  const [isFromCMS, setIsFromCMS] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    
    const fetchNavigation = async () => {
      try {
        const response = await cmsApi.getPublicNavigation();
        if (response.data && response.data.length > 0) {
          setNavigation(response.data);
          setIsFromCMS(true);
        }
      } catch (err) {
        console.log('CMS navigation not available, using defaults');
      } finally {
        setLoading(false);
      }
    };
    
    fetchNavigation();
  }, []);

  return { navigation, loading, isFromCMS };
};

export default useCMSContent;
